"use client";

import { ArrowRight, Plane, Wrench, Cog, Activity, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheHotelRevbabaProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const stages = [{
    number: "01",
    title: "DATA AUDIT",
    description: "Deep-dive analysis of your current OTA performance and market positioning.",
    Icon: Plane
  }, {
    number: "02",
    title: "STRATEGY DESIGN",
    description: "Customized revenue roadmap tailored to your property's unique demand cycles.",
    Icon: Wrench
  }, {
    number: "03",
    title: "TECH INTEGRATION",
    description: "Seamless implementation of high-conversion hospitality technology stacks.",
    Icon: Cog
  }, {
    number: "04",
    title: "YIELD OPTIMIZATION",
    description: "Active management of rates and inventory to maximize daily revenue.",
    Icon: Activity
  }, {
    number: "05",
    title: "GROWTH REPORTING",
    description: "Transparent performance tracking and continuous strategic refinement.",
    Icon: BadgeCheck
  }];
  return <section data-section-id="3930" id="the-hotelrevbaba-process" className="dark bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-4 md:px-8">
      {}
      <div className="absolute top-6 left-6 font-mono text-xs tracking-widest opacity-60">
        + 00.00 / 00.00
      </div>
      <div className="absolute top-6 right-6 font-mono text-xs tracking-widest opacity-60">
        SECTION 03 +
      </div>
      <div className="absolute bottom-6 left-6 font-mono text-xs tracking-widest opacity-60">
        + REV.STRATEGY
      </div>
      <div className="absolute bottom-6 right-6 font-mono text-xs tracking-widest opacity-60">
        REV. 2024 +
      </div>

      <div className="max-w-7xl mx-auto">
        {}
        <div className="mb-16 md:mb-24">
          <div className="font-mono text-xs md:text-sm tracking-[0.2em] mb-6 opacity-70 text-center">
            // 03 — THE REV-STRATEGY
          </div>
          <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight text-center leading-[0.95]">
            FROM AUDIT TO <span className="italic text-primary">Revenue</span> GROWTH.
          </h2>
          <div className="font-mono text-xs md:text-sm tracking-[0.2em] mt-6 opacity-70 text-center">
            A 5-STAGE CONSULTING FRAMEWORK
          </div>
        </div>

        {}
        <div ref={ref} className="relative">
          {}
          <div className="hidden lg:block relative">
            {}
            <div className="absolute top-[88px] left-[10%] right-[10%] h-px overflow-hidden">
              <div className="h-px border-t border-dashed transition-transform duration-[1800ms] ease-out origin-left" style={{
              borderColor: "var(--border)",
              transform: isVisible ? "scaleX(1)" : "scaleX(0)"
            }} />
            </div>

            <div className="grid grid-cols-5 gap-4 relative">
              {stages.map((stage, i) => {
              const Icon = stage.Icon;
              return <div data-index={i} key={stage.number} className="group flex flex-col items-center text-center transition-all duration-500" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(20px)",
                transitionDelay: `${i * 150}ms`
              }}>
                    {}
                    <div className="mb-6 h-12 flex items-center justify-center">
                      <Icon className="w-10 h-10 transition-colors duration-300 group-hover:opacity-100 opacity-80 text-primary" strokeWidth={1.25} />
                    </div>

                    {}
                    <div className="relative mb-8 z-10">
                      {}
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-px bg-border" />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-8 bg-border" />
                      {}
                      <div className="relative w-6 h-6 border-2 transition-all duration-300 group-hover:scale-110 border-border bg-background">
                        <div className="absolute inset-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-primary" />
                      </div>
                    </div>

                    {}
                    <div className="font-mono text-5xl xl:text-6xl font-light mb-3 transition-colors duration-300 group-hover:text-primary">
                      {stage.number}
                    </div>

                    {}
                    <h3 className="font-default text-xl xl:text-2xl tracking-wide mb-3 font-semibold">
                      {stage.title}
                    </h3>

                    {}
                    <p className="text-sm leading-relaxed font-default max-w-[200px] opacity-75">
                      {stage.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="lg:hidden relative">
            {}
            <div className="absolute left-6 md:left-8 top-8 bottom-8 w-px overflow-hidden">
              <div className="w-px border-l border-dashed h-full transition-transform duration-[1800ms] ease-out origin-top" style={{
              borderColor: "var(--border)",
              transform: isVisible ? "scaleY(1)" : "scaleY(0)"
            }} />
            </div>

            <div className="space-y-12">
              {stages.map((stage, i) => {
              const Icon = stage.Icon;
              return <div data-index={i} key={stage.number} className="group relative pl-16 md:pl-20 transition-all duration-500" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(-20px)",
                transitionDelay: `${i * 120}ms`
              }}>
                    {}
                    <div className="absolute left-6 md:left-8 top-2 -translate-x-1/2 z-10">
                      <div className="relative">
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-px bg-border" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-6 bg-border" />
                        <div className="relative w-5 h-5 border-2 transition-all duration-300 group-hover:scale-110 border-border bg-background">
                          <div className="absolute inset-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-primary" />
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="flex items-center gap-4 mb-3">
                      <Icon className="w-8 h-8 opacity-80 text-primary" strokeWidth={1.25} />
                      <div className="font-mono text-4xl md:text-5xl font-light transition-colors duration-300 group-hover:text-primary">
                        {stage.number}
                      </div>
                    </div>

                    {}
                    <h3 className="font-default text-xl md:text-2xl tracking-wide mb-2 font-semibold">
                      {stage.title}
                    </h3>

                    {}
                    <p className="text-sm md:text-base leading-relaxed font-default opacity-75 max-w-md">
                      {stage.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 flex flex-col items-center gap-6">
          <div className="font-mono text-xs md:text-sm tracking-[0.2em] opacity-70">
            AVG. CONSULTATION // 30–60 MINS
          </div>
          <Button variant="ghost" className="font-mono text-xs tracking-[0.2em] hover:bg-transparent group border border-primary text-primary" asChild>
            <Link to="/ContactPage">
              BOOK A CONSULTATION
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}